import React, { useEffect, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { track } from "../../lib/analytics";
import { CONTACT_EMAIL } from "../../lib/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// UI-only for this build. Named analytics hooks fire, but there is no live
// Google Sheets, email, or calendar connection yet.
const empty = {
  name: "",
  email: "",
  company: "",
  problem: "",
  duration: "",
  schedule: "",
};

export const DiagnosticDialog = ({ open, onOpenChange, context }) => {
  const [form, setForm] = useState(empty);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      track("diagnostic_started", { source: context?.query ? "find_your_switch" : "direct" });
      setForm({ ...empty, problem: context?.query || "" });
      setSubmitted(false);
      setError("");
    }
  }, [open, context]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    track("diagnostic_submitted", {
      schedule: form.schedule,
      situation_context: context?.situation || null,
    });
    try {
      const res = await fetch(`${API}/diagnostic`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          source: context?.query ? "find_your_switch" : "direct",
          situation_context: context?.situation || null,
        }),
      });
      if (!res.ok) throw new Error("bad status");
      setSubmitted(true);
    } catch (err) {
      setError(`We couldn't send this right now. Please try again, or write to ${CONTACT_EMAIL}.`);
    }
  };

  const field =
    "focus-ring w-full rounded-xl border border-charcoal/15 bg-warm-white px-4 py-3 text-[15px] text-charcoal outline-none placeholder:text-charcoal/40";

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[80] bg-charcoal/50 backdrop-blur-[2px] data-[state=open]:animate-in data-[state=open]:fade-in motion-reduce:animate-none" />
        <Dialog.Content
          data-testid="diagnostic-dialog"
          className="fixed left-1/2 top-1/2 z-[90] w-[92vw] max-w-[560px] -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-2xl bg-white shadow-2xl outline-none data-[state=open]:animate-in data-[state=open]:fade-in data-[state=open]:zoom-in-95 motion-reduce:animate-none"
        >
          <div className="flex items-start justify-between border-b px-6 py-5 hairline">
            <div>
              <Dialog.Title className="text-xl font-semibold text-charcoal">
                Bring us your growth problem
              </Dialog.Title>
              <Dialog.Description className="mt-1 text-[14px] text-charcoal/60">
                A short conversation, completed in under two minutes.
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <button
                type="button"
                aria-label="Close"
                data-testid="diagnostic-close"
                className="focus-ring flex h-9 w-9 items-center justify-center rounded-full border border-charcoal/15 text-charcoal hover:border-charcoal/40"
              >
                <X size={18} strokeWidth={1.75} />
              </button>
            </Dialog.Close>
          </div>

          {submitted ? (
            <div data-testid="diagnostic-confirm" className="px-6 py-10 text-center">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-lime text-2xl text-charcoal">
                ✓
              </div>
              <h3 className="mt-5 text-2xl font-semibold text-charcoal">Thank you.</h3>
              <p className="mx-auto mt-3 max-w-[42ch] text-[15px] leading-relaxed text-charcoal/70">
                We&rsquo;ve received your growth problem. A member of the SWITCH
                team will review it and be in touch.
              </p>
              <div className="mt-6 flex flex-wrap justify-center gap-3">
                <button
                  type="button"
                  onClick={() => onOpenChange(false)}
                  className="focus-ring rounded-full bg-charcoal px-6 py-3 text-[14px] font-medium text-warm-white"
                >
                  Close
                </button>
              </div>
              <p className="mt-5 text-[12px] uppercase tracking-wider text-charcoal/35">
                Only shortlisted enquiries will be contacted
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="max-h-[70vh] space-y-4 overflow-y-auto px-6 py-6">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-[13px] font-medium text-charcoal">Your name</label>
                  <input required data-testid="diagnostic-name" className={field} value={form.name} onChange={set("name")} placeholder="Full name" />
                </div>
                <div>
                  <label className="mb-1.5 block text-[13px] font-medium text-charcoal">Your work email</label>
                  <input required type="email" data-testid="diagnostic-email" className={field} value={form.email} onChange={set("email")} placeholder="name@company.com" />
                </div>
              </div>
              <div>
                <label className="mb-1.5 block text-[13px] font-medium text-charcoal">Company and website</label>
                <input data-testid="diagnostic-company" className={field} value={form.company} onChange={set("company")} placeholder="Company · website" />
              </div>
              <div>
                <label className="mb-1.5 block text-[13px] font-medium text-charcoal">What growth problem are you facing?</label>
                <textarea required rows={3} data-testid="diagnostic-problem" className={`${field} resize-none`} value={form.problem} onChange={set("problem")} placeholder="Describe what is not moving." />
              </div>
              <div>
                <label className="mb-1.5 block text-[13px] font-medium text-charcoal">How long has this been affecting the business?</label>
                <select data-testid="diagnostic-duration" className={field} value={form.duration} onChange={set("duration")}>
                  <option value="">Select…</option>
                  <option>Less than 3 months</option>
                  <option>3 to 12 months</option>
                  <option>Over a year</option>
                  <option>Not sure</option>
                </select>
              </div>
              <div>
                <label className="mb-1.5 block text-[13px] font-medium text-charcoal">Would you like to schedule a conversation?</label>
                <div className="flex gap-3" data-testid="diagnostic-schedule">
                  {["Yes", "Not yet"].map((opt) => (
                    <label key={opt} className={`flex-1 cursor-pointer rounded-xl border px-4 py-3 text-center text-[14px] font-medium transition-colors ${form.schedule === opt ? "border-charcoal bg-charcoal text-warm-white" : "border-charcoal/15 text-charcoal hover:border-charcoal/40"}`}>
                      <input type="radio" name="schedule" value={opt} checked={form.schedule === opt} onChange={set("schedule")} className="sr-only" />
                      {opt}
                    </label>
                  ))}
                </div>
              </div>

              <button type="submit" data-testid="diagnostic-submit" className="focus-ring mt-2 inline-flex w-full items-center justify-center gap-2 rounded-full bg-charcoal px-6 py-4 text-[15px] font-medium text-warm-white hover:bg-[#111]">
                <span className="h-[7px] w-[7px] rounded-full bg-lime" />
                Send it to SWITCH
              </button>
              {error && <p data-testid="diagnostic-error" className="text-center text-[13px] font-semibold text-[#b23b2e]">{error}</p>}
              <p className="text-center text-[12px] text-charcoal/45">
                Or write to {CONTACT_EMAIL}
              </p>
            </form>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default DiagnosticDialog;
